import React from "react";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { scaleLinear } from "d3-scale";
import {
  ComposableMap,
  Geographies,
  Geography,
  Sphere,
  Graticule,
} from "react-simple-maps";
import { useMaterialUIController } from "../../../context";
import GeographyComponent from "./GeographyComponent";
import { iso2ToName } from "./Iso2ToName";

const DARK_MAP_BG = "#0c0f14";
const DARK_OCEAN = "#0c0f14";
const DARK_STROKE = "rgba(255,255,255,0.12)";

const SimpleMap = ({ dataOverride } = {}) => {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const isDark = darkMode || theme.palette.mode === "dark";
  const mapReport = useSelector((state) => state?.report?.map);
  const data =
    (Array.isArray(dataOverride) ? dataOverride : null) ??
    mapReport?.data?.users_by_country ??
    mapReport?.users_by_country;
  if (!data || !Array.isArray(data) || data.length === 0) return null;

  const geoUrl =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
  const getCount = (d) =>
    Number(d?.Users ?? d?.users ?? d?.Count ?? d?.count ?? 0);
  const maxValue = Math.max(...data.map((d) => getCount(d)), 1);

  const colorScale = scaleLinear()
    .domain([0, maxValue])
    .range(
      isDark ? ["#3d2d3d", "#c9a0c9"] : ["#DFC2D2", "#651c66"]
    );
  const defaultFill = isDark ? "#2d2d2d" : "#edd8e1";
  const strokeColor = isDark ? DARK_STROKE : "#E4E5E6";
  const oceanFill = isDark ? DARK_OCEAN : "#f5f5f5";
  const mapBg = isDark ? DARK_MAP_BG : "transparent";

  const normalize = (str) =>
    str
      ?.toLowerCase()
      .replace(/[^a-z]+/g, "")
      .trim();

  return (
    <div
      className={isDark ? "dashboard-map-dark" : ""}
      style={{
        background: mapBg,
        borderRadius: 8,
        minHeight: 300,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isDark && (
        <style>{`.dashboard-map-dark svg.rsm-svg { background-color: ${DARK_MAP_BG} !important; background: ${DARK_MAP_BG} !important; }`}</style>
      )}
      <style>{`
        .dashboard-map-responsive svg.rsm-svg,
        .dashboard-map-dark svg.rsm-svg { width: 100% !important; height: 100% !important; }
      `}</style>
      <div className={`dashboard-map-responsive ${isDark ? "dashboard-map-dark" : ""}`} style={{ width: "100%", height: "100%", minHeight: "inherit" }}>
      <ComposableMap
        projectionConfig={{
          rotate: [-10, 0, 0],
          scale: 147,
        }}
        style={{
          marginTop: "-70px",
          marginBottom: "-70px",
          backgroundColor: mapBg,
          background: mapBg,
        }}
      >
        <Sphere fill={oceanFill} stroke={strokeColor} strokeWidth={0.5} />
        <Graticule stroke={strokeColor} strokeWidth={0.5} />

        {data?.length > 0 && (
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo, index) => {
                const countryName = geo.properties.name;

                const d = data.find((s) => {
                  const code = s.Country ?? s.country;
                  const properName = iso2ToName[code];
                  return normalize(properName) === normalize(countryName);
                });

                const count = getCount(d);

                return (
                  <GeographyComponent
                    key={index}
                    data={d}
                    geo={geo}
                    fill={d ? colorScale(count) : defaultFill}
                    isDark={isDark}
                    theme={theme}
                  />
                );
              })
            }
          </Geographies>
        )}
      </ComposableMap>
      </div>
    </div>
  );
};

export default SimpleMap;
